import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuthHeaders } from "../../services/useAuth";
import { BASE_URL } from "../../config";
import CustomButton from "../../pages/CustomButton";
import { useNavigate } from "react-router-dom";

const ProductDetails = () => {
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("");

  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/product/product-types`,
        { headers: getAuthHeaders() },
      );
      setProductTypes(response.data);
      if (response.data.length > 0) {
        setSelectedProductType(response.data[0].id || response.data[0]._id);
      }
    } catch (err) {
      setError("Failed to fetch product types");
      console.error("Error fetching product types:", err);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);

      let params = {};

      if (selectedProductType) {
        params.product_type_id = selectedProductType;
      }

      if (selectedDivision) {
        params.division_id = selectedDivision; // ✅ ADD
      }

      const response = await axios.get(`${BASE_URL}/api/product/`, {
        headers: getAuthHeaders(),
        params,
      });

      setProducts(response.data || []);
    } catch (err) {
      setError("Failed to fetch products");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (productId) => {
    navigate(`/admin/add-product?edit=${productId}`);
  };
  const fetchDivisions = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/divisions`, {
        headers: getAuthHeaders(),
      });

      setDivisions(res.data || []);

      // auto select first division
      if (res.data?.length > 0) {
        setSelectedDivision(res.data[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch divisions", err);
    }
  };

  // Filter products by active/inactive
  const filteredProducts = showActiveOnly
    ? products.filter((p) => p.is_active === true)
    : products.filter((p) => p.is_active === false);

  // Handlers
  const handleProductTypeChange = (e) => {
    setSelectedProductType(e.target.value);
  };

  const handleActiveFilterChange = (e) => {
    setShowActiveOnly(e.target.checked);
  };

  const handleGoClick = () => {
    fetchProducts();
  };

  useEffect(() => {
    fetchProductTypes();
    fetchDivisions();
  }, []);

  useEffect(() => {
    if (selectedProductType || selectedDivision) {
      fetchProducts();
    }
  }, [selectedProductType, selectedDivision]);

  if (loading && products.length === 0) {
    return (
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="p-4">
        {/* Header */}

        <div className="flex justify-between items-center border-b pb-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700"> PRODUCT </span>
            <span className="text-blue-500">DETAILS</span>
          </h1>
          <span className="text-xs text-red-600">* Means Mandatory Field</span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">
              Product Type
            </span>
            <select
              value={selectedProductType}
              onChange={handleProductTypeChange}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              {productTypes.map((type) => (
                <option key={type.id || type._id} value={type.id || type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">
              Division
            </span>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="activeProducts"
              checked={showActiveOnly}
              onChange={handleActiveFilterChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="activeProducts"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Active Products
            </label>
          </div>

          <CustomButton label="Go" onClick={handleGoClick} variant="save" />
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
      </div>

      {/* Product Details Table */}
      <div className="bg-white">
        <div className="px-6 py-4 border-b-2 border-gray-300 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            Product Management ({filteredProducts.length})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading products...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Short Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    HSN Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Mapping Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Strip Box
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Case Pack
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Packaging
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    MRP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Product Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Divisions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Composition
                  </th>
                  {/* <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                      Attachment
                    </th> */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-white border border-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product, index) => (
                  <tr key={product.id}>
                    <td className="px-4 py-4 border">{index + 1}</td>
                    <td className="px-4 py-4 border">{product.id}</td>
                    <td className="px-4 py-4 border">{product.name}</td>
                    <td className="px-4 py-4 border">{product.short_name}</td>
                    <td className="px-4 py-4 border">{product.hsn_code}</td>
                    <td className="px-4 py-4 border">{product.mapping_code}</td>
                    <td className="px-4 py-4 border">{product.strip_box}</td>
                    <td className="px-4 py-4 border">{product.case_pack}</td>
                    <td className="px-4 py-4 border">{product.packaging}</td>
                    <td className="px-4 py-4 border">
                      {product.prices?.[0]?.mrp || 0}
                    </td>
                    <td className="px-4 py-4 border">
                      {product.product_type?.name}
                    </td>
                    <td className="px-4 py-4 border">
                      {product.division_ids?.map((d) => d.name).join(", ")}
                    </td>
                    <td className="px-4 py-4 border">{product.composition}</td>
                    {/* <td className="px-4 py-4 border">
                        {product.image_path ? (
                          <a
                            href={product.image_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            View
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td> */}
                    <td className="px-4 py-4 border">
                      <button
                        onClick={() => handleEditClick(product.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No products found</p>
            <p className="text-sm">
              Select filters to view products or add new products
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
